import React, { useRef, useState } from 'react';

import { Authenticated, getLoginUrlWithRedirect } from '@opendoor/auth-fe';
import { Box, Flex, Icon, Text } from '@opendoor/bricks/core';
import { useOutsideClickAlerter } from '@opendoor/bricks/hooks/useOutsideClickAlerter';
import { colors } from '@opendoor/bricks/theme/eero';

import { useAuth, useAuthAuthenticated } from 'platform/auth-fe';
import { LegacyExternalLink } from 'platform/remix-run/react';

import { RAILS_ENV } from 'shared/helpers/globals';
import { ACCOUNT_URL } from 'shared/helpers/routes';

/**
 * The auth/account/user widget that lives at the top-right of the nav bar.
 */
export const AuthNavBarWidget: React.FC = () => {
  const authentication = useAuth();
  let content;
  if (authentication.state === 'authenticating') {
    content = null;
  } else if (authentication.state === 'authenticated' && authentication.user) {
    content = (
      <AuthenticatedContent
        authentication={{ state: authentication.state, user: authentication.user }}
      />
    );
  } else {
    // Show link to login page in both not_authenticated and error states.
    // Arguable whether this is the right thing to show on error.
    content = (
      <LegacyExternalLink
        fontSize="s0"
        fontWeight="semibold"
        href={typeof window !== 'undefined' ? getLoginUrlWithRedirect(RAILS_ENV) : '/login'}
        analyticsName="cake-auth-nav-bar-sign-in"
        aria-label={null}
      >
        Sign in
      </LegacyExternalLink>
    );
  }

  return (
    <Box
      minWidth="100px"
      height="24px"
      textAlign="right"
      transition="all ease 0.5s"
      opacity={authentication.state === 'authenticating' ? 0 : 1}
    >
      {content}
    </Box>
  );
};

const AuthenticatedContent: React.FC<{ authentication: Authenticated }> = ({ authentication }) => {
  const { logout } = useAuthAuthenticated();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  useOutsideClickAlerter(ref, () => setIsMenuOpen(false));
  // Include the Welcome text as part of the outside click alerter
  // so that the menu can be closed by clicking the Welcome text again.
  return (
    <Box ref={ref}>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- fixme(@core-experience-platform) */}
      <Flex alignItems="center" cursor="pointer" onClick={() => setIsMenuOpen((prev) => !prev)}>
        <Text mr={1} fontSize="s0" fontWeight="semibold">
          Welcome {authentication.user.firstName}
        </Text>
        <Icon name="chevron-down" size={16} />
      </Flex>
      {/* position="relative" helps us place the FloatingMenu absolutely */}
      <Box position="relative">
        <Box
          position="absolute"
          zIndex="1"
          top="8px"
          right="0"
          p={4}
          borderRadius="squared"
          boxShadow={`-5px 0 40px ${colors.neutrals40}`} // not using z2 since it's not big enough to cover the triangle
          bgColor="neutrals0"
          transition="all ease 0.3s"
          visibility={isMenuOpen ? 'visible' : 'hidden'}
          opacity={isMenuOpen ? 1 : 0}
          _before={{
            content: '""',
            position: 'absolute',
            top: '-7px',
            left: '50%',
            width: 0,
            height: 0,
            margin: '0 0 0 -10px',
            borderX: '10px solid transparent',
            borderBottom: `10px solid ${colors.neutrals0}`,
          }}
        >
          <LegacyExternalLink
            display="block"
            fontSize="s0"
            href={ACCOUNT_URL}
            target="_blank"
            analyticsName="cake-auth-nav-bar-account"
            aria-label="Opens new window with account information"
          >
            Account
          </LegacyExternalLink>
          <LegacyExternalLink
            // TODO @platform - Semantic links without href p props are a11y violations. See https://opendoor.atlassian.net/wiki/spaces/SELL/pages/1943339236/Accessibility+best+practices+for+frontend+development#Common-anti-patterns-to-look-out-for-in-progress

            className="sign-out"
            mt={4}
            display="block"
            fontSize="s0"
            onClick={logout}
            analyticsName="cake-auth-nav-bar-sign-out"
            aria-label={null}
          >
            Sign out
          </LegacyExternalLink>
        </Box>
      </Box>
    </Box>
  );
};
